// Author: Sunil Jain
// Timestamp: 2025-06-25T11:00:00EDT
// File: v4fe-physician/src/pages/Login.js
// Description: Physician Login page component with corrected login submission logic.

import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Grid,
  Link,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
// authFetch is NOT used for the login call itself, but might be used elsewhere.
// import authFetch from '../utils/authFetch'; 
import logger from '../utils/logger';

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  logger.debug("Login page rendered - ready for input.");

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };



  const handleSubmit = async (event) => {
  event.preventDefault();
  setError('');
  setLoading(true);
  logger.info(`Attempting login for email: ${email}`);

  const formData = new FormData();
  formData.append('username', email);
  formData.append('password', password);

  try {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
    // CORRECTED: The endpoint should not contain /api, as it's now in the apiBaseUrl
    const loginUrl = `${apiBaseUrl}/api/auth/login`;

    logger.info(`Sending login request to: ${loginUrl}`);

    const response = await fetch(loginUrl, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      let errorDetail = 'Login failed. Please check your credentials or network connection.';
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {
        errorDetail = `Server returned an error: ${response.status} ${response.statusText}`;
      }
      throw new Error(errorDetail);
    }
    
    const data = await response.json();

    logger.info("Login API successful, received data. Calling AuthContext login...");
    if (data && data.access_token) {
      const contextLoginSuccess = login(data);
      if (contextLoginSuccess) {
          logger.info("AuthContext updated successfully. Navigating to cockpit...");
          navigate('/');
      } else {
            logger.error("Login succeeded (API) but failed to initialize application context (AuthContext).");
            setError("Login credentials accepted, but no active role could be found for your account. Please contact support.");
            setLoading(false);
      }
    } else {
      logger.error("Login API response OK but no access_token received.");
      setError("Login successful but required token was not received.");
      setLoading(false);
    }
  } catch (err) {
    logger.error("Login.js: ERROR caught in handleSubmit:", err);
    setError(err.message);
    setLoading(false);
  }
};

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5">
          Physician Portal Sign In
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            aria-label="Email Address Input"
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            aria-label="Password Input"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
            aria-label="Sign In Button"
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
          </Button>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                Forgot password?
              </Link>
            </Grid>
             <Grid item>
                <Link component={RouterLink} to="/register-organization" variant="body2">
                  Register your organization
                </Link>
             </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}

export default Login;
